import { useState, useEffect, useRef } from 'react';
import { fetchGames } from '../lib/api';
import { mockGames } from '../data/mockGames';
import type { Game } from '../types';

export function useGames(category: string | null, searchQuery: string = '') {
  const [games, setGames] = useState<Game[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let isMounted = true;

    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await fetchGames(searchQuery);

        if (!isMounted || controller.signal.aborted) return;

        // Filter games based on category
        const filteredGames = data.filter(game => {
          const matchesCategory = !category || 
            (game.categories && game.categories.some(cat => 
              cat.toLowerCase().includes(category.toLowerCase())
            ));
          return matchesCategory;
        });

        setGames(filteredGames);
      } catch (err) {
        if (!isMounted || controller.signal.aborted) return;
        
        const errorMessage = err instanceof Error ? err.message : 'Failed to load games';
        console.error('Error fetching games:', errorMessage);
        setError(errorMessage);
        
        // Use mock data as fallback
        const filteredMockGames = mockGames.filter(game => {
          const matchesCategory = !category || 
            (game.categories && game.categories.some(cat => 
              cat.toLowerCase().includes(category.toLowerCase())
            ));
          const matchesSearch = !searchQuery || 
            game.name.toLowerCase().includes(searchQuery.toLowerCase());
          return matchesCategory && matchesSearch;
        });
        
        setGames(filteredMockGames);
      } finally {
        if (isMounted && !controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    // Debounce search queries
    const timeoutId = setTimeout(fetchData, searchQuery ? 300 : 0);

    return () => {
      clearTimeout(timeoutId);
      isMounted = false;
      controller.abort();
    };
  }, [category, searchQuery]);

  return { games, isLoading, error };
}